import Service from '@ember/service';
import { getOwner } from '@ember/application';
import * as Sentry from '@sentry/ember';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — level-controlled logging (debug/info/warn/error);
 *              dev/test logs to console as before, production routes
 *              warn/error to Sentry instead
 *   extends:   Service (@ember/service)
 *   template:  none
 *   key:       debug(...args), info(...args), warn(...args), error(...args)
 *   uses:      @sentry/ember, getOwner (to read config:environment)
 *   tests:     log-test.js
 */

/**
 * Centralized logging service (maintainability 1.1).
 *
 * Replaces scattered `console.*` calls in shipped code with level-controlled
 * logging. It mirrors the pattern already used by the global handlers in
 * `core-app.js` (`console.* ` in dev, `Sentry.capture*` in production):
 *
 *   - development / test: logs to the browser console EXACTLY as the old
 *     `console.*` calls did (behavior-preserving for local dev).
 *   - production: routes warn/error to Sentry (already initialized in
 *     core-app.js) instead of leaking to the end user's console; debug/info
 *     are dropped entirely.
 *
 * Inject with `@service log` and call `this.log.error(...)` / `.warn(...)` /
 * `.info(...)` / `.debug(...)`. Accepts the same variadic args as `console.*`.
 */
class LogService extends Service {
  get _isProd() {
    return getOwner(this)?.resolveRegistration('config:environment')?.environment === 'production';
  }
  debug(...args) {
    if (!this._isProd) console.debug(...args);
  }
  info(...args) {
    if (!this._isProd) console.info(...args);
  }
  warn(...args) {
    if (this._isProd) {
      Sentry.captureMessage(this._message(args), 'warning');
    } else {
      console.warn(...args);
    }
  }
  error(...args) {
    if (this._isProd) {
      const err = args.find(a => a instanceof Error);
      if (err) {
        Sentry.captureException(err, {
          extra: {
            log: this._message(args)
          }
        });
      } else {
        // Wrap in an Error so Sentry keeps a stack trace back to this call
        // site: a bare captureMessage arrives with zero frames and is far
        // harder to place than "undefined is not a function" already is.
        Sentry.captureException(new Error(this._message(args)));
      }
    } else {
      console.error(...args);
    }
  }

  /** Collapse variadic console-style args into a single Sentry message string. */
  _message(args) {
    return args.map(a => {
      if (a instanceof Error) return a.message;
      if (typeof a === 'string') return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    }).join(' ');
  }
}

export { LogService as default };
//# sourceMappingURL=log.js.map
